import { noteGenerationLogger } from '@heidi/session/data';
import {
  compareConfig,
  type ConfigObservation,
  type ConfigShadowOutcome,
} from './compare-config';
import { type ConfigResolution } from './resolve-config';

export type PreparedConfigShadow =
  | { kind: 'resolved'; config: ConfigResolution }
  | { kind: 'diagnostic-failed' };

export function prepareConfigShadow(
  resolve: () => ConfigResolution
): PreparedConfigShadow {
  try {
    return { kind: 'resolved', config: resolve() };
  } catch {
    return { kind: 'diagnostic-failed' };
  }
}

const diagnosticFailed = (): ConfigShadowOutcome => ({
  kind: 'diagnostic-failed',
  attributeComparisonSkipped: true,
});

// Called once, after the authoritative updater returns. Neither resolver,
// comparator nor sink exceptions can escape into generation. No awaits,
// cache writes, identifiers or raw errors. A failed compare is not a match.
export function observeConfigShadow(
  prepared: PreparedConfigShadow,
  observation: () => ConfigObservation,
  report: (outcome: ConfigShadowOutcome) => void = reportConfigShadow
): ConfigShadowOutcome {
  let outcome: ConfigShadowOutcome;
  try {
    switch (prepared.kind) {
      case 'resolved':
        outcome = compareConfig(prepared.config, observation());
        break;
      case 'diagnostic-failed':
        outcome = diagnosticFailed();
        break;
      default:
        return assertNever(prepared);
    }
  } catch {
    outcome = diagnosticFailed();
  }
  try {
    report(outcome);
  } catch {
    return diagnosticFailed();
  }
  return outcome;
}

function assertNever(value: never): never {
  void value;
  // No interpolation: even an unexpected runtime discriminant cannot leak.
  throw new Error('Unknown config shadow outcome');
}

// One bounded row per generation gives a directly observable positive
// denominator. Diagnostic failures and missing-cache attribute skips are
// explicit; silence (including sink failure) is never evidence of parity.
function reportConfigShadow(outcome: ConfigShadowOutcome): void {
  const fields = outcome.kind === 'mismatch' ? outcome.fields : [];
  noteGenerationLogger.info('note_generation_config_shadow', {
    shadow_version: 'config-v1',
    shadow_evaluated: outcome.kind !== 'diagnostic-failed',
    classification: outcome.kind,
    mismatch_fields: fields,
    mismatch_count: fields.length,
    attribute_comparison_skipped: outcome.attributeComparisonSkipped,
  });
}
